import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { CasePipe } from '../../../pipes/case.pipe';
import { FormatClaimPipe } from '../../../pipes/format-claim.pipe';
import { JoinPipe } from '../../../pipes/join.pipe';
import { LobDescriptionPipe } from '../../../pipes/lob-description.pipe';
import { BaseComponent } from '../../base/base.component';
import { WatchButtonComponent } from '../../core/buttons/watch-button.component';
import { IconComponent } from '../../core/labels/icon.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { PanelComponent } from '../../core/panels/panel.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { ClaimHeaderComponent } from '../claim-header/claim-header.component';
import { ClaimMenuComponent } from '../claim-menu/claim-menu.component';
import { FieldSearchComponent } from '../field-search/field-search.component';

@Component({
  selector: 'app-claim-layout',
  imports: [
    PanelComponent,
    CommonModule,
    DialogComponent,
    FieldSearchComponent,
    RouterModule,
    LoadingPanelComponent,
    ClaimHeaderComponent,
    LobDescriptionPipe,
    FormatClaimPipe,
    CasePipe,
    JoinPipe,
    WatchButtonComponent,
    LinkLabelComponent,
    IconComponent,
    ClaimMenuComponent,
    HomeCardComponent,
  ],
  template: `
    <div class="tw-max-w-[1300px] tw-px-4">
      <app-panel [showExpand]="false">
        <ng-template #header>
          <div class="tw-flex tw-flex-col tw-w-full tw-min-w-0">
            <div
              class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-w-full tw-gap-2"
            >
              <div class="tw-min-w-0 tw-min-h-[63px]">
                <div
                  class="tw-text-[18px] tw-text-[var(--sed-dynamic-black-color)] tw-leading-none"
                >
                  @if (lobsToLoadCoverage.includes(claim.lineOfBusiness)) {
                    {{
                      claim.lineOfBusiness
                        | lobDescription
                          : claim.coverageName
                          : claim.coverageCode
                    }}
                  } @else {
                    {{
                      claim.lineOfBusiness
                        | lobDescription: claim.type : claim.coverageCode
                    }}
                  }
                </div>
                <div class="tw-flex tw-items-center tw-flex-nowrap">
                  @if (showClaimSrOnlyHeading) {
                    <h1 class="tw-sr-only">
                      Claim
                      {{ claim.fileNumber | formatClaim: claim.fileNumFormat }}
                    </h1>
                  }
                  <h2 class="tw-break-words tw-text-[28px] sm:tw-text-[30px]">
                    {{ claim.fileNumber | formatClaim: claim.fileNumFormat }}
                  </h2>
                  <app-watch-button
                    [recordSource]="claim.recordSource"
                    [recordId]="claim.recordId"
                  />
                  <app-link-label
                    (click)="fieldDialog.visible = true"
                    [ariaLabel]="'Find claim fields'"
                  >
                    <app-icon
                      name="fa-glasses fa-light"
                      color="var(--sed-engaged-black-color)"
                      size="1.2rem"
                      class="tw-pl-3"
                    ></app-icon>
                  </app-link-label>
                  <app-dialog
                    #fieldDialog
                    label="Find claim fields"
                    titleId="field-dialog-title"
                    width="650px"
                    [dismissableMask]="true"
                  >
                    <ng-template #content>
                      <app-field-search
                        (navigate)="fieldDialog.visible = false"
                      ></app-field-search>
                    </ng-template>
                  </app-dialog>
                </div>
              </div>

              <div class="sm:tw-text-right tw-min-w-0">
                <div
                  class="tw-text-[18px] tw-text-[var(--sed-dynamic-black-color)] tw-leading-none"
                >
                  {{ claimantDesc }}
                </div>
                <h2 class="tw-break-words tw-text-[30px]">
                  @if (claim.claimantNameType?.toUpperCase() === 'I') {
                    {{
                      [
                        claim.claimantLastName,
                        claim.claimantFirstName,
                        claim.claimantMiddleName,
                      ]
                        | join
                        | case: 'titlecase'
                    }}
                  } @else {
                    {{ claim.claimantFirstName | case: 'titlecase' }}
                    {{ claim.claimantLastName | case: 'titlecase' }}
                  }
                </h2>
              </div>
            </div>
          </div>
        </ng-template>
        <div class="tw-grow tw-w-full tw-justify-center">
          <app-home-card type="Large">
            <div class="tw-pb-8 tw-w-full tw-min-w-0">
              <app-claim-header (claimRowUpdated)="onClaimRowUpdated($event)" />
              <app-claim-menu />
              <div class="tw-pt-8"></div>
              <app-loading-panel
                [state]="view.loading() ? 'Loading' : 'Loaded'"
                [loadingStyle]="'Box'"
                [loadingHeight]="400"
              />
              <div [hidden]="view.loading()">
                <router-outlet (activate)="activate($event)"></router-outlet>
              </div>
            </div>
          </app-home-card>
        </div>
      </app-panel>
    </div>
  `,
  styles: ``,
})
export class ClaimLayoutComponent extends BaseComponent implements OnInit {
  claim: ClaimRow;

  claimDesc: string;

  claimantDesc = 'Claimant';

  lobsToLoadCoverage = ['PR', 'GL', 'AU'];

  /**
   * Hidden when the child page supplies its own page h1 (e.g. Notes)
   * so the outline does not have two h1s.
   */
  showClaimSrOnlyHeading = true;

  constructor() {
    super();
  }

  onClaimRowUpdated(updatedClaim: ClaimRow): void {
    this.claim = updatedClaim;

    // Update the claimant description if employeeId changed
    if (this.claim.employeeId) {
      const employeeLabel =
        'Employee ID: ' + (this.claim.employeeId ?? 'Unknown');
      if (['DS', 'LA', 'AD'].includes(this.claim.lineOfBusiness)) {
        this.claimantDesc = employeeLabel;
      }
    }
  }

  activate(component: any) {
    // WCAG Fix for Bug 306863
    document.body.scroll({
      top: 0,
      left: 0,
      behavior: 'auto',
    });

    this.view.showLoading();
    setTimeout(() => {
      this.view.hideLoading();
    }, 10000);
  }

  ngOnInit(): void {
    // Subscribe to route data to react when resolver updates claim (handles browser back/forward)
    this.dispose.subs = this.activatedRoute.data.subscribe((data) => {
      if (data['claim']) {
        this.claim = data['claim'];
        this.updateClaimDisplay();
      }
    });

    this.updateClaimSrOnlyHeading(this.router.url);
    this.dispose.subs = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.updateClaimSrOnlyHeading(e.urlAfterRedirects));
  }

  private updateClaimSrOnlyHeading(url: string): void {
    // Notes page owns the page h1; keep sr-only claim h1 on other claim child routes.
    const path = url.split('?')[0];
    this.showClaimSrOnlyHeading = !/\/notes(?:\/|$)/.test(path);
  }

  private updateClaimDisplay(): void {
    const employeeLabel =
      'Employee ID: ' + (this.claim.employeeId ?? 'Unknown');
    this.claimantDesc = 'Claimant'; // Reset before setting based on LOB
    switch (this.claim.lineOfBusiness) {
      case 'WC':
        this.claimDesc = "Workers' Compensation";
        break;
      case 'DS':
        this.claimDesc = 'Disability';
        this.claimantDesc = employeeLabel;
        break;
      case 'AU':
        this.claimDesc = 'Auto Liability';
        break;
      case 'LA':
        this.claimDesc = 'Leave of Absence';
        this.claimantDesc = employeeLabel;
        break;
      case 'PR':
        this.claimDesc = 'Property';
        break;
      case 'PL':
        this.claimDesc = 'Professional Liability';
        break;
      case 'GL':
        this.claimDesc = 'General Liability';
        break;
      case 'AD':
        this.claimDesc = 'Accommodation Request';
        this.claimantDesc = employeeLabel;
        break;
      default:
        this.claimDesc = 'Claim';
        break;
    }
  }
}
